import { useEffect } from 'react';
import { C } from '../constants';
import { setMeta } from '../utils/meta';
import Glass from '../components/Glass';
import { Zap, Shield, Map, BarChart3 } from 'lucide-react';

const accent = C.cyan;

const STEPS = [
  { icon: <Map size={20} color={accent} />, title: 'Pick a State', body: 'Visit the site and choose from the available states. Tamil Nadu and West Bengal are live now for the 2026 assembly elections.' },
  { icon: <Zap size={20} color={accent} />, title: 'Tap Your Party', body: 'Select your district, then tap the party you support. The whole process takes about ten seconds — no login, no registration, no phone number.' },
  { icon: <Shield size={20} color={accent} />, title: 'Vote Anonymously', body: 'Your vote is recorded with no identifying information. It is stored locally on your device to prevent repeat voting — one vote per device per state.' },
  { icon: <BarChart3 size={20} color={accent} />, title: 'See Live Results', body: 'You are taken straight to the results page with a colour-coded district tile map, statewide totals, and per-district breakdowns — all updating in real time.' },
];

const FAQS = [
  {
    q: 'What Is an Anonymous Election Poll and Why Does India Need One?',
    a: 'An anonymous election poll lets citizens express party preference without sharing identity. During every election season, news channels run opinion polls conducted by agencies most voters have never heard of, using methods no one can verify. Social media fills up with unscientific polls easily gamed by bots. UnofficialElections was built to be different: radically simple, completely anonymous, and transparent in its results. It is not a replacement for scientific polling, but a complement to it — a real-time pulse check powered by the people themselves.',
  },
  {
    q: 'How Does the Platform Handle Accuracy and Prevent Manipulation?',
    a: 'UnofficialElections is a sentiment poll, not a scientific survey. Each device is limited to one vote per state using browser-based local storage. No registration, email, or phone number is required. All results are displayed openly — no editorial filter, no opaque weighting formula, and no hidden adjustments. The data you see is the raw aggregate of every vote cast.',
  },
  {
    q: 'Which States Can I Vote In Right Now?',
    a: 'Tamil Nadu (38 districts, 10 parties) and West Bengal (23 districts, 6 parties) are currently live for the 2026 assembly elections. Both pages include bilingual text in English plus the state\'s primary language.',
  },
  {
    q: 'How Can I Check District-Wise Election Poll Results?',
    a: 'On the results page, tap any district tile to see a full breakdown of party-wise vote counts for that specific district. Each district is represented as a colour-coded tile giving equal visual space to small districts like Perambalur and large ones like Villupuram.',
  },
];

const ROADMAP = [
  { state: 'Uttar Pradesh',    districts: 75,  timing: 'Feb–Mar 2027' },
  { state: 'Punjab',           districts: 23,  timing: 'Feb–Mar 2027' },
  { state: 'Gujarat',          districts: 33,  timing: 'Dec 2027' },
  { state: 'Goa',              districts: 2,   timing: 'Feb–Mar 2027' },
  { state: 'Uttarakhand',      districts: 13,  timing: 'Feb–Mar 2027' },
  { state: 'Himachal Pradesh', districts: 12,  timing: 'Nov 2027' },
  { state: 'Manipur',         districts: 16,  timing: 'Feb–Mar 2027' },
];

export default function HowItWorksScreen() {
  useEffect(() => {
    setMeta({
      title:       'India Election Poll 2026 - Free Anonymous Voting Platform | UnofficialElections',
      description: 'Vote anonymously in India\'s 2026 election poll. See live district-level results for your state. Free, fast, and open to every Indian voter.',
      ogTitle:     "How UnofficialElections Works - India's Free Anonymous Election Poll",
      ogImage:     '/og-home.png',
      canonical:   '/how-it-works',
    });
  }, []);
  return (
    <main className="px-5 md:px-10 py-10 max-w-4xl mx-auto">
      <div style={{ fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>
        Platform Guide
      </div>
      <h1 style={{
        fontWeight: 700, fontSize: 'clamp(1.8rem, 4.5vw, 3rem)',
        lineHeight: 1.05, letterSpacing: '-0.025em', marginTop: '0.4rem', color: C.white,
      }}>
        How UnofficialElections Works
      </h1>
      <p style={{ fontSize: '0.95rem', color: C.textDim, marginTop: '0.6rem', maxWidth: 620, lineHeight: 1.65 }}>
        India's free, anonymous, crowd-sourced polling platform for state elections. Vote for a party, instantly see live district-level results — no registration, no data collected.
      </p>

      {/* 4-step cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        {STEPS.map((step, i) => (
          <Glass key={i} style={{ padding: '1.2rem 1.4rem' }}>
            <div className="flex items-start gap-3">
              <div style={{
                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                background: 'rgba(0,229,255,0.08)', border: `1px solid ${accent}33`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {step.icon}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: C.white, marginBottom: '0.3rem' }}>
                  <span style={{ color: accent, marginRight: 6 }}>{i + 1}.</span>{step.title}
                </div>
                <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, margin: 0 }}>
                  {step.body}
                </p>
              </div>
            </div>
          </Glass>
        ))}
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '3rem', marginBottom: '2.5rem' }} />

      {/* FAQ sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {FAQS.map((item, i) => (
          <div key={i}>
            <h2 style={{ fontWeight: 700, fontSize: '1.1rem', color: C.white, marginBottom: '0.5rem', letterSpacing: '-0.01em' }}>
              {item.q}
            </h2>
            <p style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.75, margin: 0 }}>
              {item.a}
            </p>
          </div>
        ))}
      </div>

      {/* Roadmap table */}
      <div style={{ marginTop: '3rem' }}>
        <h2 style={{ fontWeight: 700, fontSize: '1.1rem', color: C.white, marginBottom: '0.4rem' }}>
          Roadmap: 2027 Expansion
        </h2>
        <p style={{ fontSize: '0.88rem', color: C.textDim, marginBottom: '1rem', lineHeight: 1.6 }}>
          Seven states vote in 2027. Each will be added to the platform as its election approaches, with full district maps, all contesting parties, and real-time results.
        </p>
        <Glass style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: `1px solid ${C.border}` }}>
                  {['State', 'Districts', 'Expected Timing'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: C.textDim, fontWeight: 600, fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROADMAP.map((row, i) => (
                  <tr key={i} style={{ borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
                    <td style={{ padding: '10px 16px', color: C.white, fontWeight: 600 }}>{row.state}</td>
                    <td style={{ padding: '10px 16px', color: C.textDim }}>{row.districts}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ color: accent, fontWeight: 600, fontSize: '0.8rem', background: 'rgba(0,229,255,0.08)', padding: '2px 10px', borderRadius: 999 }}>
                        {row.timing}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Glass>
        <p style={{ fontSize: '0.8rem', color: C.textDim, marginTop: '0.75rem', lineHeight: 1.6 }}>
          The long-term target is constituency-level coverage across all of India for the 2029 Lok Sabha general elections.
        </p>
      </div>

      <Glass style={{ marginTop: '2.5rem', padding: '1rem 1.3rem' }}>
        <p style={{ fontSize: '0.85rem', color: C.textDim, margin: 0, lineHeight: 1.6 }}>
          UnofficialElections.com is unofficial, non-political, and for entertainment and civic engagement only. Always exercise your real vote through the Election Commission of India's official process.
        </p>
      </Glass>
    </main>
  );
}
